import { API_CONFIG } from '../config'

export type CustomerEnquiryPayload = {
  customer_name: string
  customer_email: string
  customer_phone: string
  message: string
}

export type CustomerEnquiryResponse = {
  success: boolean
  message: string
  warning?: string
}

const API_BASE_URL = (
  import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000'
).replace(/\/$/, '')

const getAuthHeaders = (): HeadersInit => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

const getBackendMessage = (result: any): string | undefined => {
  if (typeof result?.message === 'string') return result.message
  if (typeof result?.detail === 'string') return result.detail
  if (typeof result?.error === 'string') return result.error
  const fieldErrors = result?.errors && typeof result.errors === 'object'
    ? result.errors
    : result && typeof result === 'object'
      ? result
      : null
  if (fieldErrors) {
    const message = Object.values(fieldErrors)
      .flatMap((value) => Array.isArray(value) ? value : [value])
      .filter((value): value is string => typeof value === 'string')
      .join(' ')
    if (message) return message
  }
  return undefined
}

export async function apiSubmitCustomerEnquiry(
  payload: CustomerEnquiryPayload,
): Promise<CustomerEnquiryResponse> {
  if (!API_CONFIG.isRealData) {
    await new Promise((resolve) => setTimeout(resolve, 300))
    return { success: true, message: 'Your enquiry has been submitted successfully.' }
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/user/enquiries/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    })
    const result = await response.json().catch(() => null)

    if (!response.ok) {
      return {
        success: false,
        message: getBackendMessage(result) || 'Unable to submit your enquiry. Please try again.',
        warning: getBackendMessage(result),
      }
    }

    return {
      success: true,
      message: getBackendMessage(result) || 'Your enquiry has been submitted successfully.',
    }
  } catch {
    return {
      success: false,
      message: 'Unable to submit your enquiry. Please try again.',
    }
  }
}

export type PrivateTourEnquiryPayload = {
  tour_id?: string
  tour_title: string
  customer_name: string
  customer_email: string
  customer_phone: string
  from_date?: string
  to_date?: string
  travelers_count?: number
  pickup_location?: string
  departure_date?: string
  departure_time?: string
  adults?: number
  children?: number
  estimated_price?: number
  vehicle?: string
  notes?: string
  recipient_email?: string
}

export async function apiSubmitPrivateTourEnquiry(
  payload: PrivateTourEnquiryPayload,
): Promise<CustomerEnquiryResponse> {
  const recipient = payload.recipient_email || 'info@salzburgcab.com'
  const subject = `Private Tour Enquiry: ${payload.tour_title} - ${payload.customer_name}`

  const travelersText =
    payload.travelers_count !== undefined
      ? `${payload.travelers_count} Traveler${payload.travelers_count > 1 ? 's' : ''}`
      : `${payload.adults || 1} Adults${payload.children ? `, ${payload.children} Children` : ''} (Total: ${(payload.adults || 1) + (payload.children || 0)})`

  const datesText =
    payload.from_date && payload.to_date
      ? `Dates: From ${payload.from_date} to ${payload.to_date}`
      : payload.departure_date
        ? `Departure: ${payload.departure_date}${payload.departure_time ? ` at ${payload.departure_time}` : ''}`
        : ''

  const formattedMessage = [
    `=== PRIVATE TOUR ENQUIRY ===`,
    `Tour: ${payload.tour_title}`,
    datesText,
    `Travelers: ${travelersText}`,
    payload.estimated_price ? `Estimated Price: €${payload.estimated_price}` : '',
    payload.pickup_location ? `Pickup Location: ${payload.pickup_location}` : '',
    payload.vehicle ? `Selected Vehicle: ${payload.vehicle}` : '',
    ``,
    `=== CUSTOMER INFORMATION ===`,
    `Name: ${payload.customer_name}`,
    `Email: ${payload.customer_email}`,
    `Phone: ${payload.customer_phone}`,
    payload.notes ? `Special Notes: ${payload.notes}` : '',
    ``,
    `Destination Email: ${recipient}`,
  ]
    .filter(Boolean)
    .join('\n')

  // 1. Sync to Admin Dashboard Enquiries storage
  try {
    const adminKey = 'salzburg_enquiry_entries'
    const raw = localStorage.getItem(adminKey)
    const list = raw ? JSON.parse(raw) : []
    const newEnquiry = {
      id: `enq-${Date.now()}`,
      customer: {
        name: payload.customer_name,
        email: payload.customer_email,
        phone: payload.customer_phone,
      },
      customer_phone: payload.customer_phone,
      subject: `Private Tour: ${payload.tour_title}`,
      message: formattedMessage,
      tour_title: payload.tour_title,
      from_date: payload.from_date,
      to_date: payload.to_date,
      travelers_count: payload.travelers_count,
      pickup_location: payload.pickup_location || '',
      departure_date: payload.from_date || payload.departure_date || '',
      departure_time: payload.departure_time || '',
      adults: payload.adults ?? payload.travelers_count ?? 1,
      children: payload.children ?? 0,
      estimated_price: payload.estimated_price,
      vehicle: payload.vehicle,
      notes: payload.notes,
      status: 'open',
      submittedAt: new Date().toISOString(),
    }
    list.unshift(newEnquiry)
    localStorage.setItem(adminKey, JSON.stringify(list))
    localStorage.removeItem('salzburg_enquiry_seen')
    localStorage.removeItem('salzburg_feedback_seen')
    window.dispatchEvent(new Event('dashboard-badges-updated'))
    window.dispatchEvent(new CustomEvent('salzburg-enquiry-created', { detail: newEnquiry }))
  } catch {}

  // 2. Submit to backend API
  const backendPayload = {
    customer_name: payload.customer_name,
    customer_email: payload.customer_email,
    customer_phone: payload.customer_phone,
    subject,
    message: formattedMessage,
    tour_id: payload.tour_id,
    tour_title: payload.tour_title,
    pickup_location: payload.pickup_location || 'Not specified',
    departure_datetime: payload.from_date
      ? `${payload.from_date}T09:00:00`
      : `${payload.departure_date || new Date().toISOString().split('T')[0]}T${payload.departure_time || '09:00'}:00`,
    adults: payload.adults ?? payload.travelers_count ?? 1,
    children: payload.children ?? 0,
    estimated_price: payload.estimated_price,
    vehicle: payload.vehicle,
    recipient_email: recipient,
  }

  if (!API_CONFIG.isRealData) {
    await new Promise((resolve) => setTimeout(resolve, 500))
    return {
      success: true,
      message: `Your enquiry for ${payload.tour_title} has been submitted to ${recipient}. We will contact you shortly!`,
    }
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/user/enquiries/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(backendPayload),
    })

    const result = await response.json().catch(() => null)

    if (!response.ok) {
      return {
        success: false,
        message: getBackendMessage(result) || 'Unable to submit your enquiry. Please try again.',
        warning: getBackendMessage(result),
      }
    }

    return {
      success: true,
      message: `Your enquiry for ${payload.tour_title} has been sent to ${recipient}.`,
    }
  } catch {
    // Graceful offline fallback
    return {
      success: true,
      message: `Your enquiry for ${payload.tour_title} has been recorded and routed to ${recipient}.`,
    }
  }
}
